/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { ConversationState, EntityType } from 'src/graphql/generated/graphql.generated';
import {
  ConversationMetadataRecord,
  ConversationMetadataProps,
} from 'src/store/generated/conversation-metadata-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ConversationProps {
  id: string;
  name: string;
  uniqueName: string;
  vendorConversationId: string;
  conversationState: ConversationState;
  entityId: string | null;
  entityType: EntityType | null;
  metadata: ConversationMetadataProps | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ConversationRecord extends Record<ConversationProps>({
  id: null as any,
  name: null as any,
  uniqueName: null as any,
  vendorConversationId: null as any,
  conversationState: null as any,
  entityId: null as any,
  entityType: null as any,
  metadata: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    uniqueName: string;
    vendorConversationId: string;
    conversationState: ConversationState;
    entityId: string | null;
    entityType: EntityType | null;
    metadata: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ConversationRecord {
    if (json == null) {
      throw new Error('ConversationRecord.fromJS: parameter cannot be null');
    }
    return new ConversationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ConversationJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'ConversationJson.name'),
      uniqueName: getFromOrThrow(json, (j) => j.uniqueName, 'ConversationJson.uniqueName'),
      vendorConversationId: getFromOrThrow(
        json,
        (j) => j.vendorConversationId,
        'ConversationJson.vendorConversationId',
      ),
      conversationState: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'conversationState', ConversationState.Initializing),
        'ConversationJson.conversationState',
      ),
      entityId: json.entityId,
      entityType: json.entityType,
      metadata: json.metadata != null ? new ConversationMetadataRecord(json.metadata) : null,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    uniqueName: string;
    vendorConversationId: string;
    conversationState: ConversationState;
    entityId: string | null;
    entityType: EntityType | null;
    metadata: any;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ConversationRecord, Error[]] {
    if (json == null) {
      throw new Error('ConversationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ConversationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ConversationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'ConversationJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      uniqueName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.uniqueName, 'ConversationJson.uniqueName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      vendorConversationId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.vendorConversationId,
            'ConversationJson.vendorConversationId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      conversationState: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'conversationState', ConversationState.Initializing),
            'ConversationJson.conversationState',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () => json.entityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      metadata: tryOrDefault(
        () => (json.metadata != null ? new ConversationMetadataRecord(json.metadata) : null),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ConversationProps) {
    super(props);
    if (props == null) {
      throw new Error('ConversationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ConversationViewModelBase implements ConversationProps {
  protected readonly _props: ConversationProps;
  private _json?: ConversationProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get uniqueName(): string {
    return this._props.uniqueName;
  }
  get vendorConversationId(): string {
    return this._props.vendorConversationId;
  }
  get conversationState(): ConversationState {
    return this._props.conversationState;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get entityType(): EntityType | null {
    return this._props.entityType;
  }
  get metadata(): ConversationMetadataProps | null {
    return this._props.metadata;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isActive() {
    return this._props.conversationState === ConversationState.Active;
  }
  public get isClosed() {
    return this._props.conversationState === ConversationState.Closed;
  }
  public get isInactive() {
    return this._props.conversationState === ConversationState.Inactive;
  }
  public get isInitializing() {
    return this._props.conversationState === ConversationState.Initializing;
  }
  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyConversationState(...conversationState: ConversationState[]) {
    return conversationState.includes(this._props.conversationState);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return this._props.entityType != null && entityType.includes(this._props.entityType);
  }

  constructor(props: ConversationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        uniqueName: this._props.uniqueName,
        vendorConversationId: this._props.vendorConversationId,
        conversationState: this._props.conversationState,
        entityId: this._props.entityId,
        entityType: this._props.entityType,
        metadata: this._props.metadata,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllConversationEntities = (state: AppState) =>
  state.entityStores.conversation.entities;

export const selectConversationById = (id: string) => (state: AppState) =>
  state.entityStores.conversation.entities.get(id);

export const selectConversationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.conversation.meta.get(id);

export const selectConversationAndMetadataById = (id: string) =>
  createSelector(
    selectConversationById(id),
    selectConversationMetadataById(id),
    (conversation, meta) => ({ conversation, meta }),
  );

export function selectConversationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllConversationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ConversationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectConversationEntitiesByName(name: string) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectConversationEntitiesByUniqueName(uniqueName: string) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.uniqueName === uniqueName).valueSeq(),
  );
}

export function selectConversationEntitiesByVendorConversationId(vendorConversationId: string) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.vendorConversationId === vendorConversationId).valueSeq(),
  );
}

export function selectConversationEntitiesByConversationState(
  conversationState: ConversationState,
) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.conversationState === conversationState).valueSeq(),
  );
}

export function selectConversationEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectConversationEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllConversationEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

//#endregion Selectors
